import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Leaf, Languages, Star, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LOGO_URL, HERO_IMG } from "@/lib/brand";

const TrustChip = ({ icon: Icon, label, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300"
  >
    <Icon className="w-3.5 h-3.5 text-[#FF1F87]" />
    {label}
  </div>
);

export default function Hero() {
  const { t } = useI18n();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden"
    >
      {/* Pink glow blobs */}
      <div className="glow-blob bg-[#EC008C] w-[520px] h-[520px] -top-32 -left-24" />
      <div className="glow-blob bg-[#FF1F87] w-[420px] h-[420px] top-40 -right-24 opacity-40" />
      <div className="grain" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* LEFT: Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/40 mb-7">
            <Sparkles className="w-3.5 h-3.5 text-[#FF1F87] sparkle" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FF66B3]">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
            <span className="chrome-text">{t.hero.titleA}</span>
            <br />
            <span className="pink-text">{t.hero.titleB}</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed">
            {t.hero.sub}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-2 bg-[#EC008C] hover:bg-[#FF1F87] text-white px-7 py-4 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(236,0,140,0.45)]"
            >
              {t.hero.primary}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#pricing"
              data-testid="hero-pricing-cta"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-white/5 transition-all"
            >
              {t.hero.secondary}
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-2.5">
            <TrustChip icon={ShieldCheck} label={t.hero.trustA} testId="trust-insured" />
            <TrustChip icon={Languages} label={t.hero.trustB} testId="trust-bilingual" />
            <TrustChip icon={Leaf} label={t.hero.trustC} testId="trust-eco" />
            <TrustChip icon={Star} label={t.hero.trustD} testId="trust-satisfaction" />
          </div>
        </motion.div>

        {/* RIGHT: Logo & image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_120px_-30px_rgba(236,0,140,0.45)]">
            <img
              src={HERO_IMG}
              alt="Pristine clean home"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#EC008C]/30 via-transparent to-transparent" />

            {/* Floating logo */}
            <div className="absolute inset-x-0 top-6 flex justify-center slow-float">
              <div className="w-44 h-44 rounded-3xl overflow-hidden bg-black/80 backdrop-blur-xl border border-white/15 shadow-2xl">
                <img src={LOGO_URL} alt="Rosas logo" className="w-full h-full object-contain p-1" />
              </div>
            </div>

            {/* Bottom stats card */}
            <div className="absolute bottom-5 left-5 right-5 bg-black/60 backdrop-blur-xl border border-white/15 rounded-2xl p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-1 text-[#FF1F87]">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                    <span className="text-white text-xs ml-1 font-semibold">4.9</span>
                  </div>
                  <p className="text-xs text-zinc-300 mt-1">+1,280 hogares en NJ & NY</p>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black pink-text leading-none">
                    24/7
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                    Soporte
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Floating sparkle */}
          <Sparkles className="absolute -top-4 -left-4 w-10 h-10 text-[#FF1F87] sparkle" />
          <Sparkles className="absolute -bottom-4 -right-2 w-8 h-8 text-white/70 sparkle" />
        </motion.div>
      </div>
    </section>
  );
}
